import { useEffect, useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { parseUrlState, buildUrlParams, hasUrlParamsChanged } from "./utils";
/**
 * Hook to parse initial table state from URL parameters
 *
 * @param enableUrlState - Whether URL state management is enabled
 * @param initialPageSize - Default page size
 * @param tableId - Optional table identifier for namespacing URL parameters
 * @returns Parsed URL state containing sorting, filters, and pagination
 */ export function useParseUrlState(enableUrlState, initialPageSize, tableId) {
    const searchParams = useSearch({
        strict: false
    });
    return useMemo(()=>{
        if (!enableUrlState) {
            return {
                sorting: [],
                columnFilters: [],
                pageIndex: 0,
                pageSize: initialPageSize,
                columnVisibility: {}
            };
        }
        return parseUrlState(searchParams, initialPageSize, tableId);
    }, [
        enableUrlState,
        searchParams,
        initialPageSize,
        tableId
    ]);
}
/**
 * Hook to sync table state changes back to URL parameters
 *
 * Watches for changes in sorting, filters, and pagination, then updates
 * the URL with debouncing to prevent excessive updates.
 *
 * @param options - Configuration options for URL state synchronization
 *
 * @example
 * ```tsx
 * useSyncTableStateToUrl({
 *   enableUrlState: true,
 *   initialPageSize: 10,
 *   sorting,
 *   columnFilters,
 *   pagination: table.state.pagination,
 *   tableId: 'assignments'
 * });
 * ```
 */ export function useSyncTableStateToUrl(options) {
    const { enableUrlState, initialPageSize, sorting, pagination, columnVisibility, tableId } = options;
    const navigate = useNavigate();
    const searchParams = useSearch({
        strict: false
    });
    useEffect(function syncTableStateToUrl() {
        if (!enableUrlState) return;
        const timeout = setTimeout(()=>{
            const newParams = buildUrlParams({
                searchParams,
                sorting,
                pagination,
                initialPageSize,
                columnVisibility,
                tableId
            });
            // Only navigate if params have changed
            if (hasUrlParamsChanged(newParams)) {
                // replace:true prevents back-button trapping inside debounced filter-typing states.
                // Type assertion needed for generic component - TanStack Router expects route-specific types
                navigate({
                    search: newParams,
                    replace: true
                });
            }
        }, 300); // Debounce 300ms
        return ()=>clearTimeout(timeout);
    }, [
        sorting,
        pagination,
        columnVisibility,
        enableUrlState,
        navigate,
        initialPageSize,
        searchParams,
        tableId
    ]);
}

//# sourceMappingURL=useDataTableUrlState.js.map